import React, { useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import Header from './header';
import Body, { BodyProps } from './body';
import PageHeader from './pageHeader';
import NavbarAd from '../ads/NavbarAd';
import BackToTop from '../foundation/backToTop';
import Breadcrumbs from './Breadcrumbs/Breadcrumbs';
import AppRoot from './AppRoot';
import { LoggedInUser } from '../../../types/auth.types';
import Navbar from './Navbar/Navbar';
import Footer from './Footer/Footer';
import { logoutUser } from '../../utils/AuthService';
import { setCookie, getCookie } from '../infra/storageUtils';
import ModalsManager from './ModalsManager';

declare global {
    interface Window {
        /**
         * google analytis
         */
        ga?: any
        /**
         * google tag manager
         */
        dataLayer?: {
            push: (obj: any) => void
        }
        /**
         * google ads
         */
        adsbygoogle?: any[]
        modals?: {
            toggleRegisterModal: (options?: { hideSmartAgent?: boolean }) => void
            toggleLoginModal: () => void
            toggleEmployerRegisterModal: () => void
        }
    }
}

export type PageProps = {
    url: string;
    pageHeaderTitle?: string;
    user?: LoggedInUser;
    showAds?: boolean;
    showBreadCrumbs?: boolean;
    breadCrumbs?: { name?: string; text?: string; url: string }[];
    beforeBody?: JSX.Element;
    className?: string;
    children: JSX.Element | JSX.Element[];
    crumbsSide?: JSX.Element;
    showBackToTop?: boolean;
    showPageHeader?: boolean;
    showNavbarExpressBadge?: boolean;
} & BodyProps;

const Page: React.FC<PageProps> = ({
    url,
    pageHeaderTitle,
    user,
    showAds = false,
    showBreadCrumbs = true,
    breadCrumbs,
    beforeBody,
    className,
    children,
    crumbsSide,
    showBackToTop = true,
    showPageHeader = true,
    showNavbarExpressBadge = false,
    ...bodyProps
}) => {
    const getPageHeaderTitle = () => showPageHeader && pageHeaderTitle && <PageHeader title={pageHeaderTitle} />;

    const getNavbarAd = () => showAds && <NavbarAd />;

    const getBreadCrumbs = () => {
        if (breadCrumbs) return breadCrumbs;
        return [{ text: pageHeaderTitle, url }];
    };

    const logout = (ev?: React.MouseEvent) => {
        if (ev) ev.preventDefault();
        logoutUser();
        window.location.assign('/auth/logout');
    };

    const setSentryUser = useCallback(() => {
        if (user) {
            Sentry.setUser(user);
        }
    }, [user]);

    const setGTMDataLayer = useCallback(() => {
        const { dataLayer } = window;
        if (!dataLayer) window.dataLayer = [];
        if (user) {
            window.dataLayer.push({
                userId: user.userid,
                event: 'setUserId',
            });
        }

        const gtmScript = (w: any, d: Document, s: string, l: string, i: string) => {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js',
            });
            const f = d.getElementsByTagName(s)[0];
            const j = d.createElement(s);
            const dl = l !== 'dataLayer' ? `&l=${l}` : '';
            j.async = true;
            j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
            f.parentNode?.insertBefore(j, f);
        };

        gtmScript(window, document, 'script', 'dataLayer', 'GTM-KDNGP32');
    }, [user]);

    useEffect(() => {
        setGTMDataLayer();
        setSentryUser();

        const geolocation = getCookie('geolocation');

        if (!geolocation && window.navigator.geolocation) {
            window.navigator.geolocation.getCurrentPosition(
                position => {
                    const jobsCriteria = getCookie('jobsCriteria');
                    if (!jobsCriteria) {
                        setCookie('jobsCriteria', JSON.stringify({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        }));
                    }

                    setCookie('geolocation', JSON.stringify({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    }));
                },
                () => {
                    setCookie('geolocation', JSON.stringify(undefined));
                },
            );
        }
    }, [setGTMDataLayer, setSentryUser]);

    return (
        <AppRoot user={user}>
            {beforeBody}
            <ModalsManager />
            <section className={className}>
                <Header url={url} user={user}>
                    <Navbar logoutClick={logout} showExpressBadge={showNavbarExpressBadge} />
                    {getPageHeaderTitle()}
                    {showBreadCrumbs && (
                        <Breadcrumbs sideChildrens={crumbsSide} crumbs={getBreadCrumbs()} />
                    )}
                    {getNavbarAd()}
                </Header>
                <div id="ariel-gtm-container" />
                <Body {...bodyProps}>{children}</Body>
                <Footer />
                {showBackToTop && <BackToTop />}
            </section>
        </AppRoot>
    );
};

export default Page;
